import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { logger } from '~/lib/logger';

// ─── BarcodeDetector type declarations (Chromium Shape Detection API) ─────────
// Available in Chrome 83+, Edge 83+, Opera 70+.  Not available in Firefox/Safari.

interface DetectedBarcode {
  rawValue: string;
  format: string;
  boundingBox: DOMRectReadOnly;
}

declare class BarcodeDetectorClass {
  constructor(options?: { formats?: string[] });
  detect(source: ImageBitmapSource): Promise<DetectedBarcode[]>;
}

// Map react-native-vision-camera code types → BarcodeDetector format names
const CODE_TYPE_MAP: Record<string, string> = {
  'ean-13': 'ean_13',
  'ean-8': 'ean_8',
  'code-128': 'code_128',
  'code-39': 'code_39',
  aztec: 'aztec',
  codabar: 'codabar',
  'code-93': 'code_93',
  'data-matrix': 'data_matrix',
  itf: 'itf',
  'pdf-417': 'pdf417',
  qr: 'qr_code',
  'upc-a': 'upc_a',
  'upc-e': 'upc_e',
};

// ─── ZXing fallback for PDF417 decoding ──────────────────────────────────────
// Chrome's BarcodeDetector often detects PDF417 position but returns empty
// rawValue on macOS.  We use @zxing/browser as a JS-based fallback.
let zxingReaderPromise: Promise<import('@zxing/browser').BrowserPDF417Reader> | null = null;

function getZxingPDF417Reader() {
  if (!zxingReaderPromise) {
    zxingReaderPromise = import('@zxing/browser').then((mod) => {
      logger.log('[Camera Web] ZXing PDF417 reader loaded');
      return new mod.BrowserPDF417Reader();
    });
  }
  return zxingReaderPromise;
}

/** Try to decode a barcode from a canvas using ZXing (JS-based). */
async function zxingDecodeFromCanvas(
  canvas: HTMLCanvasElement,
): Promise<{ value: string; type: string } | null> {
  try {
    const reader = await getZxingPDF417Reader();
    // decodeFromCanvas expects an HTMLCanvasElement
    const result = await reader.decodeFromCanvas(canvas);
    if (result?.getText()) {
      return { value: result.getText(), type: 'pdf417' };
    }
  } catch {
    // ZXing throws NotFoundException when no barcode found — this is normal
  }
  return null;
}

// ─── Camera component ─────────────────────────────────────────────────────────
// Calls getUserMedia on mount (isActive=true), which triggers the browser's
// native permission popup — matching iOS/Android behaviour where the OS dialog
// appears automatically when the camera screen opens, with no extra button press.

interface WebCameraRef {
  takePhoto: (_opts?: object) => Promise<{ path: string }>;
}

interface CodeScannerConfig {
  codeTypes?: string[];
  onCodeScanned?: (codes: Array<{ value: string; type: string }>) => void;
}

interface WebCameraProps {
  style?: object;
  device?: unknown;
  isActive?: boolean;
  codeScanner?: CodeScannerConfig;
  [key: string]: unknown;
}

export const Camera = React.forwardRef<WebCameraRef, WebCameraProps>((props, ref) => {
  const { style, isActive } = props;
  const codeScanner = props.codeScanner as CodeScannerConfig | undefined;
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [denied, setDenied] = useState(false);

  // ── Media stream setup ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isActive) {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      return;
    }
    setDenied(false);
    const start = (constraints: MediaStreamConstraints) =>
      navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      });

    // Request at least 1280×720 — PDF417 barcodes (SA licence discs) need
    // decent resolution for reliable detection.
    start({
      video: {
        facingMode: 'environment',
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
      audio: false,
    })
      .catch(() =>
        start({
          video: { width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        }),
      )
      .catch(() => start({ video: true, audio: false }))
      .catch((err: Error) => {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setDenied(true);
        }
      });

    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [isActive]);

  // ── Barcode scanning via BarcodeDetector API ────────────────────────────
  // We track the codeScanner callback in a ref so a stable identity doesn't
  // prevent the effect from firing.  The effect re-runs whenever isActive or
  // the codeScanner *prop* changes (the prop switches between a config object
  // and undefined depending on the active tab).
  const codeScannerRef = useRef(codeScanner);
  codeScannerRef.current = codeScanner;

  useEffect(() => {
    logger.log('[Camera Web] Barcode effect — isActive:', isActive, 'codeScanner:', !!codeScanner);
    if (!codeScanner?.onCodeScanned || !isActive) {
      logger.log('[Camera Web] Barcode scanning skipped — no callback or inactive');
      return;
    }

    const BarcodeDetector = (
      globalThis as unknown as {
        BarcodeDetector?: typeof BarcodeDetectorClass;
      }
    ).BarcodeDetector;

    const codeTypes = codeScanner.codeTypes ?? [];
    const formats = codeTypes.map((t) => CODE_TYPE_MAP[t]).filter(Boolean);
    logger.log(
      '[Camera Web] Requested codeTypes:',
      codeTypes,
      '→ BarcodeDetector formats:',
      formats,
    );

    // BarcodeDetector (Chromium Shape Detection API) is unavailable on
    // WebKit — i.e. *every* browser on iOS, since Safari/Chrome/Brave all
    // run WebKit there. When it's missing we fall back to the pure-JS ZXing
    // PDF417 decoder, which is the format SA licence discs use. The scan
    // loop below runs either way; `detector` simply stays null on iOS.
    let detector: InstanceType<typeof BarcodeDetectorClass> | null = null;
    if (BarcodeDetector) {
      try {
        detector = new BarcodeDetector(formats.length > 0 ? { formats } : undefined);
        logger.log('[Camera Web] BarcodeDetector available, setting up scanner');
      } catch (e) {
        logger.error('[Camera Web] Failed to create BarcodeDetector:', e);
        // Some formats may not be supported — retry without format filter
        try {
          detector = new BarcodeDetector();
          logger.log('[Camera Web] Created BarcodeDetector without format filter (fallback)');
        } catch (e2) {
          logger.error('[Camera Web] BarcodeDetector fallback also failed:', e2);
          detector = null;
        }
      }
    } else {
      logger.warn(
        '[Camera Web] BarcodeDetector API not available (iOS/WebKit) — using ZXing PDF417 fallback.',
      );
    }

    const callback = codeScanner.onCodeScanned;
    let running = true;
    let timer: ReturnType<typeof setTimeout>;
    let scanCount = 0;
    let loggedResolution = false;

    // Reusable offscreen canvas for capturing full-resolution frames.
    // Detecting on a canvas snapshot is more reliable than passing the
    // <video> element directly — some BarcodeDetector implementations
    // sample at display size, not the native video resolution.
    let scanCanvas: HTMLCanvasElement | null = null;
    let scanCtx: CanvasRenderingContext2D | null = null;

    const scan = async () => {
      const video = videoRef.current;
      if (!running) return;
      if (!video || video.readyState < 2) {
        if (scanCount < 3)
          logger.log(
            '[Camera Web] Video not ready, readyState:',
            video?.readyState,
            '— retrying in 500ms',
          );
        timer = setTimeout(scan, 500);
        return;
      }

      // Log actual video resolution once
      if (!loggedResolution) {
        loggedResolution = true;
        logger.log(`[Camera Web] Video resolution: ${video.videoWidth}×${video.videoHeight}`);
      }

      try {
        scanCount++;

        // Capture full-resolution frame to offscreen canvas
        const vw = video.videoWidth || 1280;
        const vh = video.videoHeight || 720;
        if (!scanCanvas || scanCanvas.width !== vw || scanCanvas.height !== vh) {
          scanCanvas = document.createElement('canvas');
          scanCanvas.width = vw;
          scanCanvas.height = vh;
          scanCtx = scanCanvas.getContext('2d', { willReadFrequently: true });
        }
        scanCtx!.drawImage(video, 0, 0, vw, vh);

        // Detect on the canvas (full native resolution). On iOS/WebKit there
        // is no BarcodeDetector, so `detector` is null and we go straight to
        // ZXing below.
        const barcodes = detector ? await detector.detect(scanCanvas) : [];
        if (scanCount <= 5 || scanCount % 50 === 0) {
          logger.log(`[Camera Web] Scan #${scanCount}: found ${barcodes.length} barcode(s)`);
        }

        // Check if BarcodeDetector returned a valid result
        const hasValue = barcodes.some((b) => b.rawValue && b.rawValue.trim().length > 0);

        if (hasValue && running) {
          const mapped = barcodes
            .filter((b) => b.rawValue && b.rawValue.trim().length > 0)
            .map((b) => ({
              value: b.rawValue,
              type: b.format,
            }));
          logger.log(
            '[Camera Web] Barcode detected (native):',
            `${mapped[0]?.value?.substring(0, 50)}...`,
            'type:',
            mapped[0]?.type,
          );
          if (mapped.length > 0) callback(mapped);
        } else if (running) {
          // Either BarcodeDetector detected a shape but returned an empty
          // value, found nothing, or isn't available at all (iOS) — fall back
          // to the pure-JS ZXing PDF417 decoder.
          //
          // When there's no native detector (iOS) ZXing is the *only* path,
          // so run it every scan. With a native detector, throttle to every
          // 3rd scan (or when it found a shape) to keep CPU usage reasonable.
          const runZxing = !detector || scanCount % 3 === 0 || barcodes.length > 0;
          if (runZxing) {
            const zxResult = await zxingDecodeFromCanvas(scanCanvas);
            if (zxResult && running) {
              logger.log(
                '[Camera Web] Barcode detected (ZXing fallback):',
                `${zxResult.value.substring(0, 50)}...`,
                'type:',
                zxResult.type,
              );
              callback([zxResult]);
            }
          }
        }
      } catch (err) {
        if (scanCount <= 3) logger.warn('[Camera Web] detect() error:', err);
      }
      if (running) timer = setTimeout(scan, 300);
    };

    // Give the video stream a moment to initialise before first scan
    timer = setTimeout(scan, 800);
    return () => {
      logger.log('[Camera Web] Stopping barcode scanner after', scanCount, 'scans');
      running = false;
      clearTimeout(timer);
      scanCanvas = null;
      scanCtx = null;
    };
  }, [isActive, codeScanner]);

  // ── takePhoto ───────────────────────────────────────────────────────────
  // Returns a data: URL instead of a blob: URL so the image survives page
  // refreshes (blob: URLs are ephemeral and lost on navigation/reload).
  useImperativeHandle(ref, () => ({
    takePhoto: (_opts?: object): Promise<{ path: string }> => {
      const video = videoRef.current;
      if (!video) return Promise.reject(new Error('Camera not ready'));
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 1280;
      canvas.height = video.videoHeight || 720;
      canvas.getContext('2d')!.drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      return Promise.resolve({ path: dataUrl });
    },
  }));

  if (denied) {
    return (
      <View style={[styles.denied, style as object]}>
        <Text style={styles.deniedText}>
          Camera access was denied.{'\n'}Enable it in your browser's site settings.
        </Text>
      </View>
    );
  }

  return (
    <View style={[{ overflow: 'hidden' }, style as object]}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    </View>
  );
});
Camera.displayName = 'Camera';

const styles = StyleSheet.create({
  denied: {
    flex: 1,
    backgroundColor: '#000',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  deniedText: {
    color: '#fff',
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
  },
});

// ─── useCameraDevice ──────────────────────────────────────────────────────────
// Returns a synthetic device immediately so {device && <Camera>} renders on
// the first paint and getUserMedia fires without waiting for enumerateDevices.

export function useCameraDevice(position?: string) {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) return null;
  return { id: 'web', position: position ?? 'back' };
}

// ─── useCameraPermission ──────────────────────────────────────────────────────
// Always returns hasPermission:true on web — the Camera component handles the
// browser permission popup directly via getUserMedia, so CameraPermissionGate
// passes through to children immediately (same flow as the native OS dialog).

export function useCameraPermission() {
  return {
    hasPermission: true as boolean,
    requestPermission: () => Promise.resolve(true),
  };
}

// ─── useCodeScanner ──────────────────────────────────────────────────────────
// On web, uses the browser's BarcodeDetector API (Chrome/Edge 83+).
// The returned config is passed to <Camera codeScanner={...}> which runs the
// actual detection loop.  The callback ref ensures the latest closure is used.

interface CodeScannerOptions {
  codeTypes?: string[];
  onCodeScanned?: (codes: Array<{ value: string; type: string }>) => void;
}

export function useCodeScanner(options: CodeScannerOptions) {
  const callbackRef = useRef(options.onCodeScanned);
  callbackRef.current = options.onCodeScanned;

  // Stable config object — only the callback ref changes, not the object identity,
  // so the Camera scanning effect doesn't restart on every render.
  const config = useRef<CodeScannerConfig>({
    codeTypes: options.codeTypes ?? [],
    onCodeScanned: (...args) => callbackRef.current?.(...args),
  });
  config.current.codeTypes = options.codeTypes ?? [];
  return config.current;
}
